import { useState } from 'react';

export interface DomainListEditorProps {
  domains: string[];
  onChange: (domains: string[]) => void;
  label: string;
  placeholder?: string;
}

export function DomainListEditor({
  domains,
  onChange,
  label,
  placeholder = '*.example.com',
}: DomainListEditorProps) {
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  function validate(domain: string): string | null {
    const trimmed = domain.trim();
    if (!trimmed) return 'Domain cannot be empty';
    if (/\s/.test(trimmed)) return 'Domain cannot contain spaces';
    if (domains.includes(trimmed)) return 'Domain already added';
    return null;
  }

  function handleAdd() {
    const trimmed = input.trim();
    const validationError = validate(trimmed);
    if (validationError) {
      setError(validationError);
      return;
    }
    onChange([...domains, trimmed]);
    setInput('');
    setError(null);
  }

  function handleRemove(index: number) {
    onChange(domains.filter((_, i) => i !== index));
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  }

  return (
    <div>
      <label className="block text-sm font-medium text-text-primary">
        {label}
      </label>

      {/* Chips */}
      {domains.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {domains.map((domain, index) => (
            <span
              key={`${domain}-${index}`}
              className="inline-flex items-center gap-1 rounded-full border border-border-dark bg-surface-2 px-2.5 py-0.5 text-xs font-medium text-text-primary"
            >
              {domain}
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-text-tertiary transition-colors hover:bg-[rgba(248,113,113,0.2)] hover:text-[var(--color-error)]"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Input row */}
      <div className="mt-2 flex items-center gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1 rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={!input.trim()}
          className="rounded-lg bg-surface-2 px-3 py-2 text-sm font-medium text-text-primary transition-colors hover:bg-[rgba(6,182,164,0.15)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Add
        </button>
      </div>

      {error && (
        <p className="mt-1 text-xs text-[var(--color-error)]">{error}</p>
      )}
    </div>
  );
}
